import React, { useState, useEffect } from 'react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import {
  TextField,
  Button,
  Box,
  Typography,
  Paper,
  Grid,
  Stepper,
  Step,
  AppBar,
  Toolbar,
  StepLabel,
  useTheme,
  useMediaQuery,
  Alert,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import { styled } from '@mui/system';
import { useAuth } from '../contexts/AuthContext';
import LogoutButton from '../components/LogoutButton';
import { StudentProfile } from '../types/auth.types';
import { getActivePublicRegions } from '../services/regions.services';

// Define Region interface
interface Region {
  id: string;
  name: string;
  location: string;
  state: string;
  schools: string[];
}

//RegistrationData interface
interface RegistrationData {
  firstName: string;
  lastName: string;
  school: string;
  customSchool: string;
  grade: string;
  phoneticName: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  regionId: string;
}

const validationSchema = Yup.object().shape({
  firstName: Yup.string().required('First name is required'),
  lastName: Yup.string().required('Last name is required'),
  grade: Yup.number()
    .required('Grade is required')
    .min(1, 'Grade must be at least 1')
    .max(12, 'Grade must be at most 12'),
  phone: Yup.string()
    .matches(/^[0-9]{10}$/, 'Phone number must be 10 digits')
    .required('Phone number is required'),
  address: Yup.string().required('Address is required'),
  city: Yup.string().required('City is required'),
  state: Yup.string().required('State is required'),
  zipCode: Yup.string()
    .matches(/^[0-9]{5}$/, 'Zip code must be 5 digits')
    .required('Zip code is required'),
  regionId: Yup.string().required('Region is required'),
  school: Yup.string().required('School is required'),
  customSchool: Yup.string().when('school', {
    is: 'other',
    then: (schema) => schema.required('Please enter your school name'),
    otherwise: (schema) => schema,
  }),
});

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(6),
  },
}));

const StyledForm = styled(Form)({
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem',
});

const StudentRegistration: React.FC = () => {
  //Use useAuth to get authStatus and completeRegistration function
  const { authStatus, completeRegistration } = useAuth();
  //useTheme and useMediaQuery to handle responsive design
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  //Steps for the stepper panel
  const [activeStep, setActiveStep] = useState(0);
  const steps = ['Personal Info', 'Contact Info', 'Region Info'];
  //Hooks for regions and available schools
  const [regions, setRegions] = useState<Region[]>([]);
  const [availableSchools, setAvailableSchools] = useState<string[]>([]);
  //Hook for registration errors and loading messages
  const [isLoading, setIsLoading] = useState(true);
  const [registrationError, setRegistrationError] = useState<string | null>(
    null
  );

  // Load regions data when component mounts
  useEffect(() => {
    fetchRegions();
  }, []);

  //Values for the form
  const initialValues: RegistrationData = {
    firstName: '',
    lastName: '',
    school: '',
    customSchool: '',
    grade: '',
    phoneticName: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    regionId: '',
  };

  //Get all active regions
  const fetchRegions = async () => {
    setIsLoading(true);
    try {
      //Get all active regions from the database
      const rawData = await getActivePublicRegions();

      //Map the data to the Region interface
      const data: Region[] = rawData.map((region: any) => ({
        id: region.id,
        name: region.name,
        location: region.location,
        state: region.state,
        //Ensure schools is at least an empty array if not defined yet
        schools: region.schools || [],
      }));
      //Set the regions hook with the fetched data
      setRegions(data);
    } catch (error) {
      console.error('Error fetching regions:', error);
      setRegistrationError('Failed to load regions. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  //When the user completes their registration and signs up
  const handleSubmit = async (
    values: RegistrationData,
    { setSubmitting }: any
  ) => {
    try {
      //Reset Error message
      setRegistrationError(null);

      //Check if the user is authenticated
      if (!authStatus.user) {
        throw new Error('User not authenticated');
      }

      //If the user choose "other" in the school dropdown and enters a new one in, set the school as the customSchool value
      const selectedSchool =
        values.school === 'other' ? values.customSchool : values.school;

      //Create the registration data object and set the userType to 'student'
      const registrationData: Partial<StudentProfile> = {
        ...values,
        school: selectedSchool,
        userType: 'student',
        updatedAt: new Date(),
        registrationComplete: true,
        grade: parseInt(values.grade, 10), // Convert string to number
      };

      //Submit registration data to complete the registration process (After Registration is completed, AccessGuard will handle navigation to student dashboard)
      await completeRegistration(registrationData);
    } catch (error) {
      console.error('Registration error:', error);
      setRegistrationError(
        'An error occurred during registration. Please try again.'
      );
      setSubmitting(false);
    }
  };

  return (
    <>
      <AppBar position="static" color="transparent" elevation={0}>
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Typography variant="h6" component="div">
            Student Registration
          </Typography>
          <LogoutButton />
        </Toolbar>
      </AppBar>
      <Box sx={{ maxWidth: 600, margin: 'auto', mt: 4, px: 2 }}>
        <StyledPaper elevation={3}>
          <Typography variant="h4" gutterBottom align="center" color="primary">
            Complete Your Registration
          </Typography>

          <Stepper
            activeStep={activeStep}
            alternativeLabel={!isMobile}
            orientation={isMobile ? 'vertical' : 'horizontal'}
            sx={{ mb: 4 }}
          >
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {registrationError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {registrationError}
            </Alert>
          )}

          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({
              errors,
              touched,
              isSubmitting,
              setFieldValue,
              values,
              validateForm,
              setTouched,
            }) => (
              <StyledForm>
                {activeStep === 0 && (
                  <>
                    <Field
                      as={TextField}
                      fullWidth
                      name="firstName"
                      label="First Name"
                      error={touched.firstName && Boolean(errors.firstName)}
                      helperText={touched.firstName && errors.firstName}
                    />
                    <Field
                      as={TextField}
                      fullWidth
                      name="lastName"
                      label="Last Name"
                      error={touched.lastName && Boolean(errors.lastName)}
                      helperText={touched.lastName && errors.lastName}
                    />
                    <Field
                      as={TextField}
                      fullWidth
                      name="phoneticName"
                      label="Phonetic Name (Optional)"
                      placeholder="e.g., SAIR-ah JOHN-son"
                      error={
                        touched.phoneticName && Boolean(errors.phoneticName)
                      }
                      helperText={
                        touched.phoneticName
                          ? errors.phoneticName
                          : 'How your name should be pronounced at the ceremony'
                      }
                    />
                    <Field
                      as={TextField}
                      fullWidth
                      name="grade"
                      label="Grade Level"
                      type="number"
                      InputProps={{ inputProps: { min: 1, max: 12 } }}
                      error={touched.grade && Boolean(errors.grade)}
                      helperText={touched.grade && errors.grade}
                    />
                  </>
                )}

                {activeStep === 1 && (
                  <>
                    <Field
                      as={TextField}
                      fullWidth
                      name="phone"
                      label="Phone Number"
                      error={touched.phone && Boolean(errors.phone)}
                      helperText={touched.phone && errors.phone}
                    />
                    <Field
                      as={TextField}
                      fullWidth
                      name="address"
                      label="Address"
                      error={touched.address && Boolean(errors.address)}
                      helperText={touched.address && errors.address}
                    />
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Field
                          as={TextField}
                          fullWidth
                          name="city"
                          label="City"
                          error={touched.city && Boolean(errors.city)}
                          helperText={touched.city && errors.city}
                        />
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <Field
                          as={TextField}
                          fullWidth
                          name="state"
                          label="State"
                          error={touched.state && Boolean(errors.state)}
                          helperText={touched.state && errors.state}
                        />
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <Field
                          as={TextField}
                          fullWidth
                          name="zipCode"
                          label="Zip Code"
                          error={touched.zipCode && Boolean(errors.zipCode)}
                          helperText={touched.zipCode && errors.zipCode}
                        />
                      </Grid>
                    </Grid>
                  </>
                )}

                {activeStep === 2 && (
                  <>
                    <FormControl
                      fullWidth
                      error={touched.regionId && Boolean(errors.regionId)}
                    >
                      <InputLabel id="region-select-label">Region</InputLabel>
                      <Field
                        as={Select}
                        labelId="region-select-label"
                        id="region-select"
                        name="regionId"
                        label="Region"
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                          const regionId = e.target.value;
                          setFieldValue('regionId', regionId);

                          // Reset school selection when region changes
                          setFieldValue('school', '');
                          setFieldValue('customSchool', '');

                          // Find the selected region and update available schools
                          const selectedRegion = regions.find(
                            (r) => r.id === regionId
                          );
                          if (selectedRegion) {
                            setAvailableSchools(selectedRegion.schools || []);
                          } else {
                            setAvailableSchools([]);
                          }
                        }}
                      >
                        {isLoading ? (
                          <MenuItem disabled value="">
                            Loading regions...
                          </MenuItem>
                        ) : regions.length > 0 ? (
                          regions.map((region) => (
                            <MenuItem key={region.id} value={region.id}>
                              {region.name} ({region.location}, {region.state})
                            </MenuItem>
                          ))
                        ) : (
                          <MenuItem disabled value="">
                            No regions available
                          </MenuItem>
                        )}
                      </Field>
                      {touched.regionId && errors.regionId && (
                        <Typography
                          color="error"
                          variant="caption"
                          sx={{ ml: 2, mt: 0.5 }}
                        >
                          {errors.regionId}
                        </Typography>
                      )}
                    </FormControl>

                    {/* School dropdown - only show if a region is selected */}
                    {values.regionId && (
                      <FormControl
                        fullWidth
                        error={touched.school && Boolean(errors.school)}
                        sx={{ mt: 2 }}
                      >
                        <InputLabel id="school-select-label">School</InputLabel>
                        <Field
                          as={Select}
                          labelId="school-select-label"
                          id="school-select"
                          name="school"
                          label="School"
                        >
                          {/* Add schools if available */}
                          {availableSchools.length > 0 ? (
                            availableSchools.map((school) => (
                              <MenuItem key={school} value={school}>
                                {school}
                              </MenuItem>
                            ))
                          ) : (
                            <MenuItem
                              disabled
                              value=""
                              sx={{ color: 'text.secondary' }}
                            >
                              No schools defined in this region
                            </MenuItem>
                          )}
                          <MenuItem value="other">Other (Specify)</MenuItem>
                        </Field>
                        {touched.school && errors.school && (
                          <Typography
                            color="error"
                            variant="caption"
                            sx={{ ml: 2, mt: 0.5 }}
                          >
                            {errors.school}
                          </Typography>
                        )}
                      </FormControl>
                    )}

                    {/* Custom School input field - only show if "Other" is selected */}
                    {values.school === 'other' && (
                      <Field
                        as={TextField}
                        fullWidth
                        name="customSchool"
                        label="Enter School Name"
                        margin="normal"
                        error={
                          touched.customSchool && Boolean(errors.customSchool)
                        }
                        helperText={touched.customSchool && errors.customSchool}
                      />
                    )}
                  </>
                )}

                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    mt: 2,
                  }}
                >
                  <Button
                    variant="outlined"
                    color="primary"
                    disabled={activeStep === 0}
                    onClick={() => setActiveStep((prev) => prev - 1)}
                  >
                    Back
                  </Button>
                  {activeStep === steps.length - 1 ? (
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Submitting...' : 'Complete Registration'}
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={async () => {
                        const allErrors = (await validateForm()) as Record<
                          string,
                          string | undefined
                        >;
                        const fieldsToCheck =
                          activeStep === 0
                            ? ['firstName', 'lastName', 'grade']
                            : activeStep === 1
                              ? ['phone', 'address', 'city', 'state', 'zipCode']
                              : [
                                  'regionId',
                                  'school',
                                  ...(values.school === 'other'
                                    ? ['customSchool']
                                    : []),
                                ];

                        const touchedFields = fieldsToCheck.reduce(
                          (acc, field) => {
                            acc[field] = true;
                            return acc;
                          },
                          {} as Record<string, boolean>
                        );
                        setTouched(touchedFields);

                        const hasStepErrors = fieldsToCheck.some(
                          (field) => allErrors[field]
                        );
                        if (!hasStepErrors) {
                          setActiveStep((prev) => prev + 1);
                        }
                      }}
                    >
                      Next
                    </Button>
                  )}
                </Box>
              </StyledForm>
            )}
          </Formik>
        </StyledPaper>
      </Box>
    </>
  );
};

export default StudentRegistration;
